import { useState, useEffect } from 'react';
import { syncEngine } from '@lib/sync-engine';
import { useOnline } from './useOnline';

export function useSync() {
  const { isOnline } = useOnline();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Actualizar contador de pendientes
  useEffect(() => {
    const updatePendingCount = async () => {
      const count = await syncEngine.getPendingCount();
      setPendingCount(count);
    };

    updatePendingCount();

    // Actualizar cada 10 segundos
    const interval = setInterval(updatePendingCount, 10000);

    return () => clearInterval(interval);
  }, []);

  // Sincronización manual
  const sync = async () => {
    if (!isOnline) {
      throw new Error('No hay conexión a internet');
    }

    if (isSyncing) {
      return; // Ya hay una sincronización en progreso
    }

    try {
      setIsSyncing(true);
      const result = await syncEngine.syncAll();
      setLastSyncTime(new Date());
      setPendingCount(0);
      return result;
    } catch (error) {
      console.error('Error en sincronización:', error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  // Sincronización automática cuando vuelve internet
  useEffect(() => {
    if (isOnline && pendingCount > 0 && !isSyncing) {
      if (import.meta.env.DEV) console.log('🌐 Internet detectado - sincronizando automáticamente...');
      sync().catch(console.error);
    }
  }, [isOnline]);

  return {
    sync,
    isSyncing,
    pendingCount,
    lastSyncTime,
    isOnline,
    hasPending: pendingCount > 0,
  };
}
